import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  createStudent,
  deleteStudent,
  fetchALLStudent,
  updateStudentDetail,
  bulkCreateStudents,
  getParentByStudentUsid,
  exportStudentsToCSVApi,
} from "../../actions/student";
import FullPageSpinner from "../layout/FullPageSpinner";
import PopupDialog from "../layout/PopupDialog";
import ParentEditDialog from "../layout/parentEditDialog";
import { formatDateTime, reverseTransform, transform } from "../../services/helper";
import ConfirmModal from "../layout/ConfirmModal";
import SizeHistoryModal from "../layout/SizeHistoryModal";
import { toast } from "react-toastify";
import {
  boardingStatusList,
  campusList,
  classList,
  genderList,
  houseList,
  sectionList,
  statusList,
  studentTypeList,
  ROLES,
} from "../../utils/constants";
import { utils, read, write } from "xlsx";
import { fetchAcademicYears } from "../../actions/product";
import { updateParent } from "../../actions/auth";

const StudentManagement = () => {
  const { user } = useSelector((state) => state.auth);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [studentFilterType, setStudentFilterType] = useState("USID");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [selectedId, setSelectedId] = useState();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSizeHistory, setShowSizeHistory] = useState(false);
  const [historyStudent, setHistoryStudent] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({
    show: false,
    success: 0,
    failed: 0,
    errors: []
  });

  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortKey, setSortKey] = useState("usid");
  const [sortOrder, setSortOrder] = useState("ASC");
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState("");
  const [purchaseFilter, setPurchaseFilter] = useState("");
  const [statusReport, setStatusReport] = useState({ purchased: 0, pending: 0 });

  const hasPermission = (permission) => {
    if (user?.role === ROLES.SUPER_ADMIN) return true;
    return user?.permissions?.[permission] === true;
  };

  const getAcademicYears = async () => {
    try {
      const response = await fetchAcademicYears();
      if (response) {
        setAcademicYears(response);
        const current = response.find((y) => y.is_current);
        if (current) {
          setSelectedAcademicYear(current.year);
        } else if (response.length > 0) {
          setSelectedAcademicYear(response[0].year);
        }
      }
    } catch (error) {
      console.error("Error fetching academic years:", error);
    }
  };

  useEffect(() => {
    getAcademicYears();
  }, []);

  const getStudents = React.useCallback(async () => {
    if (!hasPermission("student_view")) {
      setStudents([]);
      setTotalPages(1);
      setTotalRecords(0);
      return;
    }
    setLoading(true);
    try {
      if(!selectedAcademicYear){
        return;
      }
      const response = await fetchALLStudent(
        currentPage,
        itemsPerPage,
        searchTerm,
        studentFilterType,
        sortKey,
        sortOrder,
        selectedAcademicYear,
        purchaseFilter
      );

      if (response && response.students) {
        setStudents(response.students);
        setTotalPages(response.totalPages || 1);
        setTotalRecords(response.total || 0);
        setStatusReport({
          purchased: response.purchasedCount || 0,
          pending: response.pendingCount || 0,
        });
      } else if (Array.isArray(response)) {
        // Fallback for old API format if it returns just an array
        setStudents(response);
        setTotalPages(1);
        setTotalRecords(response.length);
      } else {
        setStudents([]);
        setTotalPages(1);
        setTotalRecords(0);
      }
    } catch (error) {
      setStudents([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, studentFilterType, sortKey, sortOrder, selectedAcademicYear, purchaseFilter]);

  useEffect(() => {
    getStudents();
  }, [getStudents]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, studentFilterType, itemsPerPage, selectedAcademicYear, purchaseFilter]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setSortKey(key);
      setSortOrder("ASC");
    }
  };

  const renderSortArrow = (key) => {
    if (sortKey === key) {
      return sortOrder === "ASC" ? " 🔼" : " 🔽";
    }
    return null;
  };

  const handleExportStudents = async () => {
    setLoading(true);
    try {
      const blob = await exportStudentsToCSVApi();
      if (blob && (blob.size > 0 || blob.length > 0)) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "Students_Details.csv");
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 100);
        toast.success("Students exported successfully");
      } else {
        toast.warning("No data found to export");
      }
    } catch (error) {
      console.error("Error exporting students:", error);
      toast.error("Failed to export students");
    } finally {
      setLoading(false);
    }
  };

  const startYear = 2015;
  const currentYear = new Date().getFullYear();

  const admissionYears = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => {
      const year1 = startYear + i;
      const year2 = year1 + 1;
      return {
        key: `${year1}-${year2}`,
        label: `${year1}-${year2}`,
      };
    }
  );


  const addStudentClick = () => {
    const dropdownData = {
      boardingStatus: boardingStatusList,
      gender: genderList,
      studentType: studentTypeList,
      house: houseList,
      class: classList,
      section: sectionList,
      campus: campusList,
    };

    const data = [
      {
        label: "USID",
        value: "",
        editable: true,
        options: null,
      },
      {
        label: "Student Name",
        value: "",
        editable: true,
        options: null,
      },
      {
        label: "Student Type",
        value: "",
        editable: true,
        options: dropdownData.studentType,
      },
      {
        label: "Admission Year",
        value: "",
        editable: true,
        options: admissionYears,
      },
      {
        label: "Boarding Status",
        value: "",
        editable: true,
        options: dropdownData.boardingStatus,
      },
      {
        label: "Gender",
        value: "",
        editable: true,
        options: dropdownData.gender,
      },
      {
        label: "Campus",
        value: "",
        editable: true,
        options: dropdownData.campus,
      },
      {
        label: "Class",
        value: "",
        editable: true,
        options: dropdownData.class,
      },
      {
        label: "Section",
        value: "",
        editable: true,
        options: dropdownData.section,
      },
      {
        label: "Address",
        value: "",
        editable: true,
        options: null,
      },
      {
        label: "House",
        value: "",
        editable: true,
        options: dropdownData.house,
      },
      {
        label: "Phone Number",
        value: "",
        editable: true,
        options: null,
        maxLength: 11,
      },
      {
        label: "Parent Name",
        value: "",
        editable: true,
        options: null,
      },
      {
        label: "Email",
        value: "",
        editable: true,
        options: null,
      },
    ];
    setSelectedId(null);
    setSelectedRow(data);
    setShowPopup(true);
  };

  const onDeleteClick = (id) => {
    setSelectedId(id);
    setShowConfirm(true);
  };

  const deleteConfirmClick = async () => {
    setLoading(true);
    await deleteStudent(selectedId);
    setShowConfirm(false);
    setLoading(false);
    setStudents(students.filter((student) => student.id !== selectedId));
    setSelectedId(null);
  };

  const onEditStudentClick = (data) => {
    const nonEditableFields = ["usid"];
    const skipKeys = ["id","createdAt","updatedAt"];
    const dropdownData = [
      {
        boardingStatus: boardingStatusList,
      },
      {
        gender: genderList,
      },
      {
        studentType: studentTypeList,
      },
      { admissionYear: admissionYears },
      { house: houseList },
      { class: classList },
      { section: sectionList },
      { campus: campusList },
      { isActive: statusList },
    ];
    setSelectedId(data.usid);
    const result = transform(data, skipKeys, nonEditableFields, dropdownData);
    setSelectedRow(result);
    setShowPopup(true);
  };

  const onEditParentClick = async (data) => {
    try {
      setLoading(true);
      const parentInfo = await getParentByStudentUsid(data.usid);
      setLoading(false);
      const nonEditableFields = ["usid", "parentName", "parentId", "role"];
      const skipKeys = ["id","created_at","updated_at"];

      setSelectedId(-1);

      const studentData = {
        parentId: parentInfo.id.toString(),
        parentName: parentInfo.parentName,
        phoneNumber: parentInfo.phoneNumber,
        email: parentInfo.email,
        password: '',
        role: parentInfo.role,
      };

      const result = transform(studentData, skipKeys, nonEditableFields);
      setSelectedRow(result);
      setShowPopup(true);
    } catch (error) {
      console.error("Error in edit student process:", error);
      toast.error("Failed to load student information", { position: "top-right" });
    }
  };

  const handleSave = async (updatedData) => {
    try {
      setLoading(true);
      const apiBody = reverseTransform(updatedData);
      if (selectedId) {
        const response = await updateStudentDetail(apiBody, selectedId);
        if (response && response.success) {
          setStudents((prevStudents) =>
            prevStudents.map((student) =>
              student.id === response.student.id ? response.student : student
            )
          );
          setSelectedRow({});
          setShowPopup(false);
          toast.success("Student updated successfully!", {
            position: "top-right",
          });
        }
      } else {
        const newStudent = await createStudent(apiBody);
        if (
          newStudent &&
          newStudent.results &&
          newStudent.results[0] &&
          newStudent.results[0].success
        ) {
          setStudents([newStudent.results[0].student, ...students]);
          setSelectedRow({});
          setShowPopup(false);
        }
      }
    } catch (error) {
      console.error("Error saving student:", error);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  };

  const handleParentSave = async (updatedData) => {
    try {
      setLoading(true);
      const apiBody = reverseTransform(updatedData);
      const parentId = apiBody.parentId;
      if (!parentId) {
        toast.error("Parent ID not found", { position: "top-right" });
        return;
      }

      const parentData = {
        id: Number(parentId),
        parentName: apiBody.parentName,
        phoneNumber: apiBody.phoneNumber,
        email: apiBody.email,
        role: apiBody.role || "parent",
      };

      // Only include password if it was changed
      if (apiBody.password && apiBody.password?.trim() !== "") {
        parentData.password = apiBody.password;
      }

      const response = await updateParent(parentData);
      if (response) {
        setSelectedRow({});
        setShowPopup(false);
      }
    } catch (error) {
      console.error("Error saving parent:", error);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  };

  // No longer needed: client-side filter and pagination logic
  const paginatedStudents = students;

  const downloadCSVTemplate = () => {
    // Make sure these headers match exactly with the fields processed in handleBulkUpload
    const headers = [
      "USID",
      "Student Name",
      "Student Type",
      "Admission Year",
      "Boarding Status",
      "Gender",
      "Campus",
      "Class",
      "Section",
      "Address",
      "House",
      "Phone Number",
      "Parent Name",
      "Email",
    ];

    // Create workbook and main sheet
    const wb = utils.book_new();
    const mainSheet = utils.aoa_to_sheet([headers]);
    utils.book_append_sheet(wb, mainSheet, "Student Template");

    // Add reference sheets for dropdown values
    const dropdownData = {
      "Student Type": studentTypeList.map(item => [item.key]),
      "Boarding Status": boardingStatusList.map(item => [item.key]),
      "Gender": genderList.map(item => [item.key]),
      "Campus": campusList.map(item => [item.key]),
      "Class": classList.map(item => [item.key]),
      "Section": sectionList.map(item => [item.key]),
      "House": houseList.map(item => [item.key]),
      "Admission Year": admissionYears.map(item => [item.key]),
    };

    // Generate worksheet for instructions
    const instructionsData = [
      ["Instructions for filling the template:"],
      ["1. Do not modify the headers or sheet structure"],
      ["2. Fill in all required fields for each student"],
      ["3. Refer to the reference sheets for valid dropdown values"],
      ["4. Save the file as Excel (.xlsx) format before uploading"],
      [""],
      ["Required Fields:"],
      ["- USID (must be unique)"],
      ["- Student Name"],
      ["- Class"],
      ["- Section"],
      [""],
      ["For dropdown fields, use only values from the reference sheets"],
    ];
    const instructionsSheet = utils.aoa_to_sheet(instructionsData);
    utils.book_append_sheet(wb, instructionsSheet, "Instructions");

    // Add each dropdown list as a separate sheet
    Object.entries(dropdownData).forEach(([sheetName, values]) => {
      const sheet = utils.aoa_to_sheet(values);
      utils.book_append_sheet(wb, sheet, `${sheetName} Values`);
    });

    // Generate binary and download
    const wbout = write(wb, { bookType: "xlsx", type: "binary" });

    // Convert to blob and create download link
    const buf = new ArrayBuffer(wbout.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < wbout.length; i++) {
      view[i] = wbout.charCodeAt(i) & 0xFF;
    }

    const blob = new Blob([buf], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "student_upload_template.xlsx";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = read(data, { type: 'array' });

        // Get the first sheet which contains student data
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const studentsData = utils.sheet_to_json(worksheet);

        // Check if data is valid
        if (!studentsData.length) {
          toast.error("No student data found in file");
          setLoading(false);
          return;
        }

        // Validation setup
        const mandatoryFields = ["USID", "Student Name", "Class", "Section"];
        const errors = [];
        const validStudents = [];
        let hasErrors = false;

        // Helper function to check if value exists in dropdown options
        const isValidDropdownValue = (key, value) => {
          if (!value) return true; // Empty values are handled by mandatory field check

          const dropdownMappings = {
            "Student Type": studentTypeList,
            "Boarding Status": boardingStatusList,
            "Gender": genderList,
            "Campus": campusList,
            "Class": classList,
            "Section": sectionList,
            "House": houseList,
            "Admission Year": admissionYears,
          };

          if (!dropdownMappings[key]) return true;
          return dropdownMappings[key].some(item => item.key === value);
        };

        // Validate each student record
        studentsData.forEach((studentData, index) => {
          const rowNum = index + 2; // +2 because of header row and 0-indexing
          const rowErrors = [];

          // Check mandatory fields
          mandatoryFields.forEach(field => {
            if (!studentData[field] || studentData[field].toString().trim() === '') {
              rowErrors.push(`Missing mandatory field "${field}"`);
            }
          });

          // Check dropdown values
          Object.entries(studentData).forEach(([key, value]) => {
            if (
              ["Student Type", "Boarding Status", "Gender", "Campus",
                "Class", "Section", "House", "Admission Year"].includes(key) &&
              value &&
              !isValidDropdownValue(key, value)
            ) {
              rowErrors.push(`Invalid value "${value}" for "${key}"`);
            }
          });

          if (rowErrors.length > 0) {
            hasErrors = true;
            errors.push(`Row ${rowNum}: ${rowErrors.join(", ")}`);
          } else {
            // Transform data to match API format
            const apiStudentData = {
              usid: studentData['USID']?.toString()?.replace(/['"]/g, '') || '',
              studentName: studentData['Student Name'] || '',
              studentType: studentData['Student Type'] || '',
              admissionYear: studentData['Admission Year'] || '',
              boardingStatus: studentData['Boarding Status'] || '',
              gender: studentData['Gender'] || '',
              campus: studentData['Campus'] || '',
              class: studentData['Class'] || '',
              section: studentData['Section'] || '',
              address: studentData['Address'] || '',
              house: studentData['House'] || '',
              phoneNumber: studentData['Phone Number'] || '',
              parentName: studentData['Parent Name'] || '',
              email: studentData['Email'] || '',
            };
            validStudents.push(apiStudentData);
          }
        });

        // If there are validation errors, show them and stop
        if (hasErrors) {
          setUploadStatus({
            show: true,
            success: 0,
            failed: studentsData.length,
            errors
          });
          setLoading(false);
          return;
        }

        // If validation passes, proceed with bulk upload
        try {
          const bulkData = {
            students: validStudents
          };

          const response = await bulkCreateStudents(bulkData);

          // Extract detailed errors from response
          const apiErrors = [];
          if (response.results && Array.isArray(response.results)) {
            response.results.forEach((result, index) => {
              if (!result.success) {
                // Find the corresponding student data
                const studentData = validStudents[index];
                const studentId = studentData?.usid || `Row ${index + 1}`;
                apiErrors.push(`${studentId}: ${result.error}`);
              }
            });
          }

          // Always refresh student list after operation (even if some failed)
          if (response.created > 0) {
            getStudents();
          }

          // Show all errors, including API errors
          setUploadStatus({
            show: true,
            success: response.created || 0,
            failed: response.failed || 0,
            errors: apiErrors.length > 0 ? apiErrors : [response?.message || "Bulk upload failed"]
          });
        } catch (error) {
          // API error
          setUploadStatus({
            show: true,
            success: 0,
            failed: validStudents.length,
            errors: [error.message || "Error during bulk upload"]
          });
        }

        setLoading(false);
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      toast.error(`Error uploading file: ${error.message}`);
      setLoading(false);
    }

    // Clear the file input
    event.target.value = null;
  };

  return (
    <>
      <style>{`
        .cursor-pointer { cursor: pointer; }
        .table-responsive::-webkit-scrollbar { height: 8px; }
        .table-responsive::-webkit-scrollbar-thumb { background: #dee2e6; border-radius: 4px; }
        .filter-card { transition: all 0.3s ease; }
        .action-btn { transition: transform 0.2s; white-space: nowrap; }
        .action-btn:hover { transform: translateY(-1px); }
        .table th { white-space: nowrap; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; }
        .table td { font-size: 0.9rem; }
        .pagination-container { border-top: 1px solid #dee2e6; padding-top: 1.5rem; }
      `}</style>
      {loading && <FullPageSpinner loading={loading} />}
      <div className="bg-light min-vh-100 py-4">
        <div className="container-fluid px-lg-4">
          <div className="card shadow-sm border-0 rounded-3">
            <div className="card-header bg-white border-bottom py-3 px-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h4 className="mb-0 text-primary fw-bold d-flex align-items-center">
                  <i className="bi bi-people-fill me-2"></i>Student Management
                </h4>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary rounded-pill px-3 py-2">Total: {totalRecords}</span>
                </div>
              </div>
            </div>
            
            <div className="card-body p-4">
              {/* Status Report Section */}
              <div className="row g-3 mb-4">
                <div className="col-12 col-sm-6 col-md-3">
                  <div className="card border-0 shadow-sm bg-primary text-white h-100">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="small text-white-50 text-uppercase fw-bold">Total Students</div>
                          <div className="h4 mb-0 fw-bold">{totalRecords}</div>
                        </div>
                        <div className="bg-white bg-opacity-25 rounded-circle p-2">
                          <i className="bi bi-people fs-4"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <div className="card border-0 shadow-sm bg-success text-white h-100">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="small text-white-50 text-uppercase fw-bold">Purchased</div>
                          <div className="h4 mb-0 fw-bold">{statusReport.purchased}</div>
                        </div>
                        <div className="bg-white bg-opacity-25 rounded-circle p-2">
                          <i className="bi bi-cart-check fs-4"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <div className="card border-0 shadow-sm bg-warning text-dark h-100">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="small text-black-50 text-uppercase fw-bold">Pending</div>
                          <div className="h4 mb-0 fw-bold">{statusReport.pending}</div>
                        </div>
                        <div className="bg-dark bg-opacity-10 rounded-circle p-2">
                          <i className="bi bi-cart-dash fs-4"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <div className="card border-0 shadow-sm bg-info text-white h-100">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="small text-white-50 text-uppercase fw-bold">Completion</div>
                          <div className="h4 mb-0 fw-bold">{totalRecords > 0 ? Math.round((statusReport.purchased / totalRecords) * 100) : 0}%</div>
                        </div>
                        <div className="bg-white bg-opacity-25 rounded-circle p-2">
                          <i className="bi bi-graph-up-arrow fs-4"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filters & Actions Section */}
              <div className="bg-white rounded-3 p-4 mb-4 border shadow-sm filter-card">
                <div className="row g-3 align-items-end mb-4">
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                      Search Students
                    </label>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-white border-end-0">
                        <i className="bi bi-search text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0 ps-0"
                        placeholder={`Search by ${studentFilterType}...`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-6 col-lg-2">
                    <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                      Filter Type
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={studentFilterType}
                      onChange={(e) => setStudentFilterType(e.target.value)}
                    >
                      <option value="Name">By Student Name</option>
                      <option value="USID">By USID</option>
                      <option value="Class">By Grade</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-6 col-lg-2">
                    <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                      Academic Year
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedAcademicYear}
                      onChange={(e) => setSelectedAcademicYear(e.target.value)}
                    >
                      <option value="">All Years</option>
                      {academicYears.map((year) => (
                        <option key={year.id} value={year.year}>
                          {year.year} {year.is_current ? "(Current)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12 col-md-6 col-lg-2">
                    <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                      Purchase Status
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={purchaseFilter}
                      onChange={(e) => setPurchaseFilter(e.target.value)}
                    >
                      <option value="">All Students</option>
                      <option value="true">Purchased</option>
                      <option value="false">Pending</option>
                    </select>
                  </div>

                  <div className="col-12 col-lg-3">
                    <div className="d-flex flex-wrap justify-content-lg-end gap-2">
                      <input
                        type="file"
                        id="bulkUpload"
                        className="d-none"
                        accept=".xlsx, .xls"
                        onChange={handleBulkUpload}
                      />
                      {hasPermission('student_export') && (
                        <button
                          className="btn btn-outline-info btn-sm action-btn px-3 fw-bold shadow-sm"
                          onClick={handleExportStudents}
                        >
                          <i className="bi bi-file-earmark-spreadsheet me-1"></i> Export Students
                        </button>
                      )}
                      {hasPermission('student_import') && (
                        <button
                          className="btn btn-outline-secondary btn-sm action-btn px-3 fw-bold shadow-sm"
                          onClick={() => document.getElementById('bulkUpload').click()}
                        >
                          <i className="bi bi-upload me-1"></i> Bulk Upload
                        </button>
                      )}
                      <button
                        className="btn btn-outline-primary btn-sm action-btn px-3 fw-bold shadow-sm"
                        onClick={downloadCSVTemplate}
                      >
                        <i className="bi bi-file-earmark-arrow-down me-1"></i> Template
                      </button>
                      {hasPermission('student_add') && (
                        <button
                          className="btn btn-success btn-sm action-btn px-3 fw-bold shadow-sm"
                          onClick={addStudentClick}
                        >
                          <i className="bi bi-person-plus-fill me-1"></i> Add Student
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {uploadStatus.show && (
                <div className={`alert ${uploadStatus.failed > 0 ? 'alert-warning' : 'alert-success'} alert-dismissible fade show rounded-3 shadow-sm mb-4`} role="alert">
                  <div className="d-flex align-items-center mb-2">
                    <i className={`bi ${uploadStatus.failed > 0 ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'} me-2 fs-5`}></i>
                    <h5 className="mb-0">Upload Results</h5>
                  </div>
                  <div className="row g-2">
                    <div className="col-auto">
                      <span className="badge bg-success">Success: {uploadStatus.success}</span>
                    </div>
                    {uploadStatus.failed > 0 && (
                      <div className="col-auto">
                        <span className="badge bg-danger">Failed: {uploadStatus.failed}</span>
                      </div>
                    )}
                  </div>
                  {uploadStatus.failed > 0 && (
                    <div className="mt-3 p-2 bg-white rounded border" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      <p className="small fw-bold text-muted mb-1">Error Logs:</p>
                      <ul className="list-unstyled small mb-0">
                        {uploadStatus.errors.map((error, index) => (
                          <li key={index} className="text-danger mb-1">• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <button type="button" className="btn-close" onClick={() => setUploadStatus({ ...uploadStatus, show: false })} aria-label="Close"></button>
                </div>
              )}

              {/* Table Section */}
              <div className="table-responsive rounded-3 border shadow-sm bg-white">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="cursor-pointer" onClick={() => handleSort("usid")}>USID {renderSortArrow("usid")}</th>
                      <th className="cursor-pointer" onClick={() => handleSort("studentName")}>Name {renderSortArrow("studentName")}</th>
                      <th className="cursor-pointer" onClick={() => handleSort("class")}>Grade {renderSortArrow("class")}</th>
                      <th className="cursor-pointer" onClick={() => handleSort("section")}>Section {renderSortArrow("section")}</th>
                      <th className="cursor-pointer" onClick={() => handleSort("house")}>House {renderSortArrow("house")}</th>
                      <th className="cursor-pointer" onClick={() => handleSort("gender")}>Gender {renderSortArrow("gender")}</th>
                      <th className="cursor-pointer" onClick={() => handleSort("isActive")}>Status {renderSortArrow("isActive")}</th>
                      <th className="text-center">Purchased</th>
                      <th className="text-center">Created At</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.length > 0 ? (
                      students.map((student) => (
                        <tr key={student.usid}>
                          <td className="fw-bold text-dark">{student.usid}</td>
                          <td>
                            <div className="fw-semibold">{student.studentName}</div>
                          </td>
                          <td>{student.class}</td>
                          <td>{student.section}</td>
                          <td>{student.house}</td>
                          <td>{student.gender}</td>
                          <td> 
                            <span className={`badge rounded-pill ${student?.isActive ? 'bg-success text-white' : 'bg-secondary text-white'}`}>
                              {student?.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className={`badge rounded-pill ${student?.hasPurchased ? 'bg-success text-white' : 'bg-danger text-white'}`}>
                              {student?.hasPurchased ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="text-center">
                              {formatDateTime(student.createdAt)}
                          </td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-1">
                              {hasPermission('student_edit') && (
                                <>
                                  <button
                                    className="btn btn-outline-primary btn-sm rounded-circle"
                                    onClick={() => onEditStudentClick(student)}
                                    title="Edit Student"
                                    style={{ width: '32px', height: '32px', padding: 0 }}
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-success btn-sm rounded-circle"
                                    onClick={() => onEditParentClick(student)}
                                    title="Edit Parent"
                                    style={{ width: '32px', height: '32px', padding: 0 }}
                                  >
                                    <i className="bi bi-people"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-info btn-sm rounded-circle"
                                    onClick={() => {
                                      setHistoryStudent(student);
                                      setShowSizeHistory(true);
                                    }}
                                    title="Size History"
                                    style={{ width: '32px', height: '32px', padding: 0 }}
                                  >
                                    <i className="bi bi-clock-history"></i>
                                  </button>
                                </>
                              )}
                              {hasPermission('student_delete') && (
                                <button
                                  className="btn btn-outline-danger btn-sm rounded-circle"
                                  onClick={() => onDeleteClick(student.id)}
                                  title="Delete Student"
                                  style={{ width: '32px', height: '32px', padding: 0 }}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-5 text-muted">
                          <i className="bi bi-person-x fs-1 d-block mb-2"></i>
                          No students found matching your criteria.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Section */}
              <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 gap-3">
                <div className="d-flex align-items-center gap-3 order-2 order-md-1">
                  <div className="d-flex align-items-center bg-white border rounded px-2 py-1 shadow-sm">
                    <span className="text-muted small me-2">Show</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      className="form-select form-select-sm border-0 bg-transparent fw-bold"
                      style={{ width: 'auto', outline: 'none', boxShadow: 'none' }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-muted small ms-2">entries</span>
                  </div>
                  <div className="text-muted small border-start ps-3 d-none d-sm-block">
                    Showing <span className="fw-bold text-dark">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="fw-bold text-dark">{Math.min(currentPage * itemsPerPage, totalRecords)}</span> of <span className="fw-bold text-dark">{totalRecords}</span> records
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 order-1 order-md-2">
                  <button
                    className="btn btn-outline-primary btn-sm px-3 rounded-pill shadow-sm fw-bold"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                  >
                    <i className="bi bi-chevron-left"></i> Prev
                  </button>
                  
                  <div className="bg-white px-3 py-1 rounded-pill border shadow-sm small fw-bold">
                    Page {currentPage} of {totalPages}
                  </div>

                  <button
                    className="btn btn-outline-primary btn-sm px-3 rounded-pill shadow-sm fw-bold"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                  >
                    Next <i className="bi bi-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPopup && (
        selectedId === -1 ? (
          <ParentEditDialog
            data={selectedRow}
            onSave={handleParentSave}
            onCancel={() => setShowPopup(false)}
            header={"Edit Parent Information"}
          />
        ) : (
          <PopupDialog
            data={selectedRow}
            onSave={handleSave}
            onCancel={() => setShowPopup(false)}
            header={selectedId ? "Edit Student" : "Add Student"}
          />
        )
      )}
      <SizeHistoryModal
        show={showSizeHistory}
        onHide={() => setShowSizeHistory(false)}
        studentId={historyStudent?.id}
        studentName={historyStudent?.studentName}
      />
      <ConfirmModal
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={deleteConfirmClick}
        message="Are you sure you want to delete this student?"
      />
    </>
  );
};

export default StudentManagement;
